import { useSearchParams } from "react-router";
import { Button, CheckList, FormSection, Icon, Menu, Notice, Pill, formatRelative, type MenuSection } from "@omnabase/ui";
import { AI_AGENTS, WORK_QUEUE_WORDS, isAgentId, type AgentId, type Connection, type TeamConnection } from "@omnabase/shared";
import { api } from "../../lib/api";
import { useCan } from "../../lib/access";
import { useFetch } from "../../lib/inbox";
import { useNav, useSetParam, useWs } from "../../lib/nav";
import { CONNECTOR_CHECKLIST, MCP_OFF_LINE, agentMark, cardPill, connectRefusal, connectedLine, isLive, pickedAgent, planLine, type ConnectRefusal } from "../../lib/connections";
import { useSession } from "../../shell/store";
import { ConnectDialog, MENU_AGENTS } from "./AgentSetup";
import { ConnectionRow, ConnectionsTable, ConnectorAddress, KeyDialogs, YourConnections, useCopy, useOwnConnectionActions } from "./ConnectedAi";

// Settings › Your own AI (its own section under You again since the polish round, 2026-10-31; from 2026-10-18 the AI page's tab "Your own AI
// (MCP)", AI settings round one; before that the page Settings › Your own AI: M9 round three and the "Your own
// AI" round, DECISIONS "2026-10-05 — Your own AI for every agent"). The one-card round (2026-10-23, the founder's mockup B,
// docs/design/mcp-tab-one-card.html: "way too many boxes and text; actionable stuff first"): one card "Your AI" with the state, "Connect
// Claude" and a menu for the other apps and a key, the words to say, and the consent screen's checklist; below it one table: everyone's
// connections for holders of `team` (their own among them), the member's own for everyone else. The administrator's switch stays on Engine;
// here it is only stated. The app whose steps are open lives in the address (`?connect=<app id>`, `?agent=`). The send-and-sections round
// (2026-10-23): the menu lists every app directly, then Make a key; no chooser dialog.

const initialsOf = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
type Mine = { rows: Connection[]; address: string };
type Mineful = { rows: Connection[]; loaded: boolean; reload: () => Promise<void> };

export function AiMcp() {
  const { workspace } = useSession();
  const can = useCan();
  const mine = useFetch<Mine>(`/api/v1/w/${workspace.slug}/me/connections`);
  const team = useFetch<{ rows: TeamConnection[] }>(can("team") ? `/api/v1/w/${workspace.slug}/connections` : null);
  const refusal = connectRefusal({ planHasMcp: workspace.plan.features.mcp, switchOn: workspace.settings.aiConnections });
  const list: Mineful = { rows: mine.data?.rows ?? [], loaded: !!mine.data, reload: async () => { await mine.reload(); if (can("team")) await team.reload(); } };
  const address = mine.data?.address ?? "";
  const agent = useAgent(list.rows);
  return (
    <>
      <AiCard refusal={refusal} list={list} />
      {can("team")
        ? <MembersConnections rows={team.data?.rows} loaded={!!team.data} reload={list.reload} mine={list.rows} address={refusal === null ? address : ""} />
        : refusal !== "plan" && <YourConnections open={refusal === null} list={list} address={address} />}
      {refusal === null && <ConnectDialogs address={address} />}
      {refusal === null && <KeyDialogs agent={agent} address={address} onMade={list.reload} />}
    </>
  );
}

/** the agent in the address, or the member's newest connection's, or Claude */
function useAgent(rows: Connection[]): AgentId {
  const [params] = useSearchParams();
  return pickedAgent(params.get("agent"), rows);
}

/** The card: the state pill, the newest connection or why not, the buttons, the words to say and what the AI may do. */
function AiCard({ refusal, list }: { refusal: ConnectRefusal | null; list: Mineful }) {
  const { workspace } = useSession();
  const now = Date.now();
  const newest = [...list.rows].filter((c) => isLive(c, now)).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  const pill = cardPill({ refusal, connected: !!newest, wayUp: workspace.plan.wayUp.mcp });
  return (
    <FormSection title="Your AI" action={<Pill>{pill}</Pill>} intro="Your own AI app works your queue with your rights and uses no credits.">
      {refusal === "plan" && <PlanNotice />}
      {refusal === "switch" && <OffNotice />}
      {refusal === null && newest && <p className="ob-ai-card__state"><Icon name="checkCircle" size="sm" />{connectedLine(newest, (iso) => formatRelative(iso, now))}</p>}
      <CardButtons refusal={refusal} newest={newest} list={list} />
      <PromptRow />
      <CheckList label="What your AI may do" items={CONNECTOR_CHECKLIST} />
    </FormSection>
  );
}

/** "Connect Claude", "Disconnect" once connected (the newest connection), and the menu of the other apps (each with its mark since the ticket
 *  page and marks round) and a key */
function CardButtons({ refusal, newest, list }: { refusal: ConnectRefusal | null; newest: Connection | undefined; list: Mineful }) {
  const setParam = useSetParam();
  const { revoke } = useOwnConnectionActions(list.reload);
  const off = refusal !== null;
  const open = (agent: AgentId) => { setParam("agent", agent); setParam("connect", agent, true); };
  const sections: MenuSection[] = [
    { items: MENU_AGENTS.map((id) => ({ id, label: AI_AGENTS.find((a) => a.id === id)!.label, icon: agentMark(id), onSelect: () => open(id) })) },
    { items: [{ id: "key", label: "Make a key", meta: "For apps that take one", icon: "key", onSelect: () => setParam("newkey", "1", true) }] },
  ];
  return (
    <div className="ob-ai-card__actions">
      <Button variant={newest ? "secondary" : "primary"} icon="link" disabled={off} onClick={() => open("claude")}>Connect Claude</Button>
      {newest && !off && <Button variant="ghost" onClick={() => void revoke(newest)}>Disconnect</Button>}
      <Menu align="start" sections={sections}
        trigger={<Button variant="outline" disabled={off}>{newest ? "Add another app" : "Other apps"}<Icon name="chevronDown" size="sm" /></Button>} />
    </div>
  );
}

/** the words to say to the connected AI, with Copy */
function PromptRow() {
  const copy = useCopy();
  return (
    <p className="ob-ai-card__prompt"><Icon name="bot" size="sm" /><span>Then say “{WORK_QUEUE_WORDS}”</span>
      <Button size="sm" variant="ghost" icon="copy" aria-label={`Copy "${WORK_QUEUE_WORDS}"`} onClick={() => copy(WORK_QUEUE_WORDS, "Words")}>Copy</Button></p>
  );
}

/** the administrator's switch off: the line, and the way to the switch for whoever may turn it on (never a member) */
function OffNotice() {
  const nav = useNav(); const { ws } = useWs(); const can = useCan();
  const engine = ws("/settings/ai");
  const action = can("team") || can("aiSettings") ? <Button size="sm" onClick={(e) => nav(engine, e as never)}>Open Engine</Button> : undefined;
  return <Notice icon="lock" action={action}>{MCP_OFF_LINE}</Notice>;
}

/** the plan without the feature: the plans that have it */
function PlanNotice() {
  const { workspace } = useSession();
  const can = useCan(); const nav = useNav(); const { ws } = useWs();
  const plans = ws("/settings/billing?plans=1"); // Billing is in the column of those who may buy only (2026-10-05): the rest get no button
  return <Notice icon="lock" action={can("billing") ? <Button size="sm" onClick={(e) => nav(plans, e as never)}>See plans</Button> : undefined}>{planLine(workspace.plan.wayUp.mcp, workspace.plan.name)}</Notice>;
}

/** the app whose steps are open (`?connect=<app id>`; an unknown word opens Claude's) */
function ConnectDialogs({ address }: { address: string }) {
  const [params] = useSearchParams(); const setParam = useSetParam(); const copy = useCopy();
  const connect = params.get("connect");
  if (!connect) return null;
  return <ConnectDialog agent={isAgentId(connect) ? connect : "claude"} address={address} onCopy={copy} onClose={() => setParam("connect", null)} />;
}

/** Holders of `team`: every member's connections with their last call, revocable, their own among them ("(you)", with Renew near a key's
 *  end), the connector's address above. With the switch off they stay listed, paused. */
function MembersConnections({ rows: all, loaded, reload, mine, address }: { rows: TeamConnection[] | undefined; loaded: boolean; reload: () => Promise<void>; mine: Connection[]; address: string }) {
  const { workspace, toast } = useSession();
  const own = useOwnConnectionActions(reload);
  const paused = !workspace.settings.aiConnections || !workspace.plan.features.mcp;
  const now = Date.now();
  const mineIds = new Set(mine.map((c) => c.id));
  const rows = [...(all ?? [])].sort((a, b) => a.memberName.localeCompare(b.memberName) || a.name.localeCompare(b.name));
  const photoOf = (userId: string) => workspace.members.find((m) => m.userId === userId)?.imageUrl ?? undefined;
  const revoke = async (c: TeamConnection) => {
    if (mineIds.has(c.id)) { await own.revoke(c); return; }
    try { await api.del(`/api/v1/w/${workspace.slug}/connections/${c.id}`); toast(`${c.name} revoked`, { kind: "success" }); await reload(); } catch (e) { toast((e as Error).message, { kind: "error" }); }
  };
  return (
    <FormSection title="Members' connections" audience="admins" intro="Every member's keys and signed-in apps here, yours included. Revoking one stops its AI at the next call.">
      {address && <ConnectorAddress address={address} />}
      {loaded && rows.length === 0 ? <p className="ob-form-row__help">No member has connected an AI yet.</p> : rows.length > 0 && (
        <ConnectionsTable label="Members' connections" withMember>
          {rows.map((c) => { const you = mineIds.has(c.id); const name = you ? `${c.memberName} (you)` : c.memberName; return <ConnectionRow key={c.id} c={c} now={now} member={{ name, initials: initialsOf(c.memberName), photo: photoOf(c.userId) }} paused={paused} onRenew={you && !paused ? () => void own.renew(c) : undefined} onRevoke={() => void revoke(c)} />; })}
        </ConnectionsTable>
      )}
    </FormSection>
  );
}
